import { useQuery, useQueryClient } from 'react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { graphQLClient } from '../services/authService'
import appUserQueries from '../services/graphql/appUsers/queries'
import { EndUser } from '../types/end-user'

interface Query {
  user: {
    OR: (
      | {
          email_contains: string
          displayName_contains?: undefined
        }
      | {
          displayName_contains: string
          email_contains?: undefined
        }
    )[]
  }
  location?: {
    OR: { [x: string]: string }[]
  }
  nbSharedExperiences_gte?: number
  nbSharedExperiences_lte?: number
  isSuspended?: boolean
}

export function useAppUsers(params: { [x: string]: string }) {
  return useQuery<EndUser[], any>(['endUsers', params], async () => {
    const where: Query = {
      user: { OR: [{ email_contains: params.search }, { displayName_contains: params.search }] },
    }

    if (params.location)
      where.location = {
        OR: [
          { city_contains: params.location },
          { postcode_contains: params.location },
          { country_contains: params.location },
        ],
      }
    if (params.status) where.isSuspended = params.status === 'ACTIVE' ? false : true
    if (params.nbSharedExperiences_gte) where.nbSharedExperiences_gte = Number(params.nbSharedExperiences_gte)
    if (params.nbSharedExperiences_lte) where.nbSharedExperiences_lte = Number(params.nbSharedExperiences_lte)

    const { endUsers }: { endUsers: EndUser[] | [] } = await graphQLClient.request(appUserQueries.getAppUsers, {
      where,
    })

    return endUsers
  })
}

export function useAppUser() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  return useQuery<EndUser, any>(
    ['endUser', id],
    async () => {
      const { endUser }: { endUser: EndUser } = await graphQLClient.request(appUserQueries.getAppUser, {
        where: {
          id: id,
        },
      })

      return endUser
    },
    {
      enabled: !!id,
      placeholderData: () => {
        const endUsers = queryClient.getQueryData<EndUser[]>('endUsers')

        if (!endUsers) return undefined

        return endUsers.find((user) => user.id === id)
      },
      onError: () => {
        navigate('/app-users')
      },
    }
  )
}
